import React from 'react';

import { cn } from '@/lib/utils';

export interface ChoiceSliderOption<T> {
    value: T;
    /** Two or three words. It sits under a stop, so it cannot be a sentence. */
    label: string;
    /**
     * One line about what this position does, shown under the track while it
     * is the chosen one.
     *
     * The labels on a slider are necessarily terse, and terse labels for
     * options that differ by degree are exactly where someone guesses wrong
     * and spends a generation finding out.
     */
    hint?: string;
}

interface ChoiceSliderProps<T> {
    value: T;
    options: ChoiceSliderOption<T>[];
    onChange: (value: T) => void;
    /** Sits above the track, naming the axis the positions move along. */
    caption?: string;
}

const RAIL_H = 9;
const KNOB = 24;
/** Long and soft, so the travel reads as movement rather than a state flip. */
const EASE = '260ms cubic-bezier(0.22, 1, 0.36, 1)';

/**
 * Everything is the same ink at a different strength.
 *
 * No accent colour: the slider is one small setting on a card full of them,
 * and a saturated fill makes it the loudest thing on screen — which it is not.
 * `color-mix` against the token rather than a frozen rgba, so the ramp follows
 * the theme.
 */
const INK = (alpha: number) => `color-mix(in oklab, var(--foreground) ${Math.round(alpha * 100)}%, transparent)`;

/**
 * A few positions along one axis, chosen by sliding.
 *
 * For a choice whose options are ORDERED — more of something to less of it —
 * where a row of pills says only "these are the alternatives" and a slider
 * says "these are steps along one line, and you are here". From Reference is
 * the case it was built for: how much of the vendor's photograph survives,
 * from all of it to only its look.
 *
 * Built on a rail and a knob rather than a library Slider because the value is
 * one of a few named things, not a number: it snaps to stops, and every stop
 * needs a word under it. But it is a slider, so it must be draggable — a
 * control that looks like a slider and only answers to clicks reads as broken,
 * and dragging is the gesture the shape itself promises.
 */
export function ChoiceSlider<T extends string | number>({
    value,
    options,
    onChange,
    caption,
}: ChoiceSliderProps<T>) {
    const railRef = React.useRef<HTMLDivElement | null>(null);
    const [dragging, setDragging] = React.useState(false);

    const index = Math.max(0, options.findIndex((o) => Object.is(o.value, value)));
    const chosen = options[index];
    const last = Math.max(1, options.length - 1);
    // Percentage along the rail, so fill, ticks and knob agree at every count.
    const pct = (index / last) * 100;

    /** Which stop a pointer at this x is nearest. Right-to-left aware. */
    const stopAt = React.useCallback((clientX: number): number => {
        const el = railRef.current;
        if (!el) return index;
        const rect = el.getBoundingClientRect();
        if (rect.width <= 0) return index;
        const rtl = typeof window !== 'undefined'
            && window.getComputedStyle(el).direction === 'rtl';
        const along = rtl ? rect.right - clientX : clientX - rect.left;
        const ratio = Math.min(1, Math.max(0, along / rect.width));
        return Math.round(ratio * last);
    }, [index, last]);

    const moveTo = React.useCallback((clientX: number) => {
        const next = options[stopAt(clientX)];
        // Only when it actually changes: dragging fires continuously, and every
        // repeat would be another write to the node and another render.
        if (next && !Object.is(next.value, value)) onChange(next.value);
    }, [options, stopAt, onChange, value]);

    const onPointerDown = (e: React.PointerEvent) => {
        // Pointer capture, so a drag that wanders off the rail — or off the
        // card — keeps steering it until the finger lifts.
        (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
        e.stopPropagation();
        setDragging(true);
        moveTo(e.clientX);
    };

    /**
     * The canvas starts dragging a node from `mousedown`, and a pointer event
     * does not stop the mouse event that follows it — so sliding picked up the
     * whole card and carried it across the canvas. Every interactive control
     * inside a node has to say this.
     *
     * Only the parts that are actually controls, though. Put on the whole
     * component it also claimed the caption above the rail and the description
     * below it, and a card cannot be picked up by the text on it — a dead zone
     * across the middle of the node, to fix a drag on one thin rail.
     */
    const swallowNodeDrag = (e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
    };

    const onKeyDown = (e: React.KeyboardEvent) => {
        const forward = e.key === 'ArrowRight' || e.key === 'ArrowUp';
        const back = e.key === 'ArrowLeft' || e.key === 'ArrowDown';
        if (!forward && !back) return;
        e.preventDefault();
        const next = options[Math.min(last, Math.max(0, index + (forward ? 1 : -1)))];
        if (next && !Object.is(next.value, value)) onChange(next.value);
    };

    return (
        <div className={cn('py-1', dragging && 'select-none')}>
            {caption && (
                <p className="mb-3 text-xs font-medium text-muted-foreground">{caption}</p>
            )}

            {/* Padded by half a knob, so the knob at either end sits inside the
                card rather than over its edge — and vertically, so it has room
                to sit proud of the rail instead of crowding what is above it. */}
            <div className="relative pt-2.5 pb-1" style={{ paddingInline: KNOB / 2 }}>
                <div
                    ref={railRef}
                    role="slider"
                    tabIndex={0}
                    aria-valuemin={0}
                    aria-valuemax={last}
                    aria-valuenow={index}
                    aria-valuetext={chosen?.label}
                    aria-label={caption}
                    onPointerDown={onPointerDown}
                    onPointerMove={(e) => { if (dragging) moveTo(e.clientX); }}
                    onPointerUp={() => setDragging(false)}
                    onPointerCancel={() => setDragging(false)}
                    onMouseDown={swallowNodeDrag}
                    onKeyDown={onKeyDown}
                    className={cn(
                        // The rail keeps its pill shape: a groove, not a chip.
                        'relative rounded-full outline-none',
                        // The rail is thin; the hand is not. This widens what
                        // can be grabbed without widening what is drawn.
                        'before:absolute before:-inset-x-3 before:-top-[11px] before:-bottom-[11px] before:content-[""]',
                        dragging ? 'cursor-grabbing' : 'cursor-pointer',
                    )}
                    style={{
                        height: RAIL_H,
                        backgroundColor: INK(0.05),
                        touchAction: 'none',
                        boxShadow: undefined,
                    }}
                >
                    {/* Filled portion, up to the chosen stop. Faint at its start
                        and only a little stronger at the knob — enough to see
                        how far along it is, not enough to look like a bar. */}
                    <div
                        className="absolute inset-y-0 start-0 rounded-full"
                        style={{
                            width: `${pct}%`,
                            backgroundImage: `linear-gradient(to right, ${INK(0.07)}, ${INK(0.15)})`,
                            transition: dragging ? 'none' : `width ${EASE}`,
                        }}
                    />

                    {/* Ticks — where it will land, and how far along it has come.
                        Passed stops sit inside the fill and go pale; the ones
                        ahead are hollow rings on the empty rail. Drawn, not
                        clicked: the whole rail takes the pointer. */}
                    {options.map((o, i) => {
                        const passed = i < index;
                        return (
                            <span
                                key={String(o.value)}
                                className="pointer-events-none absolute top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full"
                                style={{
                                    insetInlineStart: `${(i / last) * 100}%`,
                                    width: passed ? 6 : 8,
                                    height: passed ? 6 : 8,
                                    backgroundColor: passed ? INK(0.10) : 'transparent',
                                    boxShadow: passed ? 'none' : `inset 0 0 0 1.5px ${INK(0.12)}`,
                                    // The one under the knob would only show
                                    // through it.
                                    opacity: i === index ? 0 : 1,
                                    transition: `all ${EASE}`,
                                }}
                            />
                        );
                    })}

                    {/* The knob — background-coloured, with a quiet centre so it
                        belongs to the track it rides on. */}
                    <span
                        className="pointer-events-none absolute top-1/2 flex items-center justify-center rounded-full bg-background"
                        style={{
                            insetInlineStart: `${pct}%`,
                            transform: `translate(-50%, -50%) scale(${dragging ? 1.1 : 1})`,
                            width: KNOB,
                            height: KNOB,
                            border: `1px solid ${INK(0.12)}`,
                            boxShadow: dragging
                                ? `0 2px 8px ${INK(0.16)}, 0 0 0 5px ${INK(0.05)}`
                                : `0 1px 4px ${INK(0.12)}`,
                            transition: dragging
                                ? 'transform 140ms ease, box-shadow 140ms ease'
                                : `inset-inline-start ${EASE}, transform 140ms ease, box-shadow 140ms ease`,
                        }}
                    >
                        <span
                            className="rounded-full"
                            style={{ width: 8, height: 8, backgroundColor: INK(0.34) }}
                        />
                    </span>
                </div>

                {/* Labels, one under each stop. The end ones are pulled inside
                    the rail so they cannot overhang the card. */}
                <div className="relative mt-4 h-5">
                    {options.map((o, i) => {
                        const active = i === index;
                        const at = (i / last) * 100;
                        const translate = i === 0 ? '0' : (i === last ? '-100%' : '-50%');
                        return (
                            <button
                                key={String(o.value)}
                                type="button"
                                tabIndex={-1}
                                onMouseDown={swallowNodeDrag}
                                onClick={() => { if (!active) onChange(o.value); }}
                                className={cn(
                                    'absolute cursor-pointer border-0 bg-transparent p-0 text-xs whitespace-nowrap transition-colors hover:text-foreground',
                                    active ? 'font-medium text-foreground' : 'font-normal text-muted-foreground',
                                )}
                                style={{ insetInlineStart: `${at}%`, transform: `translateX(${translate})` }}
                            >
                                {o.label}
                            </button>
                        );
                    })}
                </div>
            </div>

            {chosen?.hint && (
                <p
                    className="mt-3.5 rounded-lg border px-2.5 py-2 text-[11px] leading-normal text-muted-foreground"
                    style={{ backgroundColor: INK(0.015), borderColor: INK(0.06) }}
                >
                    {chosen.hint}
                </p>
            )}
        </div>
    );
}
